import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { FaBan, FaTriangleExclamation } from "react-icons/fa6";
import { ListGroup, ListGroupItem, Table } from "reactstrap";
import useSecurityRoles from "../../../hook/useSecurityRoles";
import useXlsx from "../../../hook/useXlsx";
import { Pageable } from "../../../types/common";
import { Action } from "../../../types/reducer-action";
import { ISearchUsersResult } from "../../../types/user";
import { equals, trimToEmpty } from "../../../utils/commons";
import { fullNameAutoFormat } from "../../../utils/format-fullname";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import OmsToolTip from "../../common/oms-tooltip/oms-tooltip";
import PaginationBar from "../../common/pagination-bar";
import TableHeaders, { TableHeaderInfo } from "../../common/table-header";
import TableRow from "../../common/table-row";
import './style.scss';
import { useUserDispatch, useUserState } from "./user-manager-context";

export interface SecurityUsersTableRef {
  export: (filename?: string) => void;
}

interface SecurityUserInfo extends ISearchUsersResult {
  displayName: string;
  positionName: string;
  userRoleNames: string;
  scopeNames: string;
}

const TABLE_HEADERS: TableHeaderInfo<SecurityUserInfo>[] = [
  { header: 'User Name', accessor: 'displayName', presorted: 'asc', cssClass: "name-column" },
  { header: 'Position/Role', accessor: 'positionName', sortable: true, cssClass: "column-15rem" },
  { header: 'Email', accessor: 'email', sortable: true, cssClass: "email-column" },
  { header: 'Security Roles', accessor: 'userRoleNames', sortable: true, cssClass: "column-15rem" }, // not really a position column, just about the right size though
  { header: 'Organization Scope', accessor: 'scopeNames', sortable: true }
];

const SecurityUsersTable = forwardRef<SecurityUsersTableRef, Pageable>((props, ref) => {
  const { pageSize } = props;

  const { dataSet, selectedDataId } = useUserState();
  const dispatch = useUserDispatch();

  const [securityRoles] = useSecurityRoles();

  const formattedData = useMemo(() => (
    dataSet?.map(item => ({
      ...item,
      displayName: fullNameAutoFormat(item.firstName, item.middleName, item.lastName),
      positionName: item.assignment?.position?.name,
      userRoleNames: securityRoles
        .filter(role => item.assignedRoles?.some(ar => equals(ar.userRoleTypeId, role.id)))
        .map(role => role.desc).join(', '),
      scopeNames: item.assignedRoles?.map(ar => ar.orgScope?.name).join('\n')
    }) as SecurityUserInfo)
  ), [dataSet, securityRoles]);

  const [sortedData, setSortedData] = useState<SecurityUserInfo[]>([]);
  const [pagedData, setPagedData] = useState<SecurityUserInfo[]>([]);

  const [exportXlsx] = useXlsx();

  const selectItem = (item?: ISearchUsersResult) => dispatch(new Action(DataEntryEvent.SelectData, item?.id));

  useEffect(() => {
    selectItem(pagedData.find(item => item.id === selectedDataId))
  }, [pagedData, selectedDataId]);

  const handleExport = useCallback((filename?: string) => {
    if (!sortedData) return;

    const headers = [TABLE_HEADERS.map(c => c.header)];

    const rows = sortedData.map(item => [
      item.displayName,
      item.positionName,
      item.email,
      item.userRoleNames,
      item.scopeNames
    ]);

    const footers = [
      [], // blank row
      ['Total results:', sortedData.length]
    ];

    exportXlsx([...headers, ...rows, ...footers],
      trimToEmpty(filename) || `security-users-${moment(new Date()).format('YYYYMMDDHHmmss')}`,
      true);
  }, [sortedData, exportXlsx]);

  useImperativeHandle(ref, () => ({
    export(filename?: string): void {
      handleExport(filename);
    }
  }));

  return (
    <section aria-label="Search results">
      <Table striped hover id="security_users" className="fixed">
        <TableHeaders headers={TABLE_HEADERS} sortableData={formattedData} onSort={setSortedData} />

        {dataSet && dataSet.length > 0
          ?
          <tbody>
            {pagedData.map((item) =>
              <TableRow key={item.id} className={item.displayOrder < 0 ? 'row-pinned' : undefined}
                selected={item.id === selectedDataId} onSelect={() => selectItem(item)}>
                <td>
                  {item.displayName} {item.expired &&
                    <FaBan className="small mb-1 text-dark text-opacity-50" title='Inactive user' />
                  }
                </td>
                <td>
                  {item.assignment && (item.assignment.expired
                    ? <>{item.assignment.position?.name} <FaTriangleExclamation className="small mb-1 text-dark text-opacity-50" title='Inactive assignment' /></>
                    : <OmsToolTip positionId={item.assignment?.position?.id}>{item.assignment?.position?.name}</OmsToolTip>
                  )}
                </td>
                <td>{item.email}</td>
                <td>{item.userRoleNames}</td>
                <td>
                  <ListGroup flush>
                    {item.assignedRoles?.map(ar => ar.orgScope)
                      .map((scope, i) => scope &&
                        <ListGroupItem key={`${scope?.id}_${i}`} className="action-link">
                          <OmsToolTip orgId={scope?.id}>{scope?.name}</OmsToolTip>
                        </ListGroupItem>
                      )}
                  </ListGroup>
                </td>
              </TableRow>
            )}
          </tbody>
          :
          <tfoot>
            <tr>
              <td colSpan={TABLE_HEADERS.length} className="text-center">
                {dataSet?.length === 0
                  ? "Sorry, we couldn't find any results matching your search criteria."
                  : 'Search results will appear in this table.'
                }
              </td>
            </tr>
          </tfoot>
        }
      </Table>

      <PaginationBar data={sortedData} pageSize={pageSize} onPageChange={setPagedData} />
    </section>
  );
});

export default SecurityUsersTable;
